import React from 'react';
import {
    Stepper,
    Step,
    StepLabel,
    StepConnector,
    stepConnectorClasses,
    StepIconProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { WizardStepperProps } from '../types';

const CustomConnector = styled(StepConnector, {
    shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ theme, compact }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: compact ? 14 : 22,
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: theme.palette.primary.main,
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: theme.palette.primary.main,
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: '#eaeaf0',
        borderTopWidth: 2,
        borderRadius: 1,
    },
}));

const StepIconRoot = styled('div')<{
    ownerState: { completed?: boolean; active?: boolean; compact?: boolean };
}>(({ theme, ownerState }) => ({
    backgroundColor: ownerState.completed || ownerState.active
        ? theme.palette.primary.main
        : '#ccc',
    zIndex: 1,
    color: '#fff',
    width: ownerState.compact ? 28 : 44,
    height: ownerState.compact ? 28 : 44,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 'bold',
    fontSize: ownerState.compact ? '12px' : '16px',
}));

const CustomStepIcon = (props: StepIconProps & { compact?: boolean }) => {
    const { active, completed, icon, compact } = props;

    return (
        <StepIconRoot ownerState={{ completed, active, compact }}>
            {icon}
        </StepIconRoot>
    );
};

const WizardStepper = ({ steps, activeStep, highestStepReached, onStepClick, compact = false }: WizardStepperProps) => {
    const { t } = useTranslation();

    const isStepClickable = (stepId: number) => stepId !== activeStep && stepId <= highestStepReached;

    const handleStepClick = (stepIndex: number) => {
        if (isStepClickable(stepIndex) && onStepClick) {
            onStepClick(stepIndex);
        }
    };

    const getCursor = (stepId: number) => {
        if (stepId === activeStep) return 'default';
        if (stepId <= highestStepReached) return 'pointer';
        return 'not-allowed';
    };

    return (
        <Stepper
            alternativeLabel
            activeStep={activeStep}
            connector={<CustomConnector compact={compact} />}
            sx={{ pt: compact ? 3 : 2, pb: compact ? 1 : 4 }}
        >
            {steps.map((step) => (
                <Step
                    key={step.id}
                    onClick={() => handleStepClick(step.id)}
                    sx={{
                        cursor: getCursor(step.id),
                        '&.Mui-disabled': {
                            cursor: step.id <= highestStepReached ? 'pointer' : 'not-allowed',
                            pointerEvents: 'auto',
                            '& *': {
                                cursor: 'inherit',
                            },
                        },
                    }}
                >
                    <StepLabel
                        StepIconComponent={(iconProps) => (
                            <CustomStepIcon {...iconProps} compact={compact} />
                        )}
                        sx={{
                            '& .MuiStepLabel-labelContainer': {
                                mt: compact ? 0.5 : 1,
                            },
                        }}
                    >
                        <Typography
                            level="body-md"
                            sx={{
                                fontWeight: activeStep === step.id ? 'bold' : 'normal',
                                color: activeStep >= step.id ? 'text.primary' : 'text.secondary',
                            }}
                        >
                            {t(step.title)}
                        </Typography>
                        {step.subtitle && (
                            <Typography
                                level="body-sm"
                                sx={{ color: 'text.secondary' }}
                            >
                                {t(step.subtitle)}
                            </Typography>
                        )}
                    </StepLabel>
                </Step>
            ))}
        </Stepper>
    );
};

export default WizardStepper;
